import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  RiUserLine,
  RiLockLine,
  RiMapPinLine,
  RiShoppingBagLine,
  RiImageEditLine,
} from 'react-icons/ri';

import Breadcrumb from '@/components/common/Breadcrumb';
import Card       from '@/components/ui/cards/Card';
import ToastMe    from '@/components/ui/ToastMe';
import { getFileUrlSigned } from '@/services/CommonService';
import {
  getByIdCustomerService,
  updateCustomerPhotoService,
} from '@/services/CustomerServices';

import ProfileTab  from './tabs/ProfileTab';
import SecurityTab from './tabs/SecurityTab';
import AddressTab  from './tabs/AddressTab';
import OrdersTab   from './tabs/OrdersTab';
import CreditTab   from './tabs/CreditTab';

/* ------------------------------------------------------------------ */
/* Tab list                                                            */
/* ------------------------------------------------------------------ */
const TAB_ITEMS = [
  { label: 'Profile',       key: 'profile',  Icon: RiUserLine        },
  { label: 'Security',      key: 'security', Icon: RiLockLine        },
  { label: 'Address',       key: 'address',  Icon: RiMapPinLine      },
  { label: 'Orders',        key: 'orders',   Icon: RiShoppingBagLine },
  { label: 'Credit Report', key: 'credit',   Icon: RiShoppingBagLine },
];

/* ================================================================== */
/* Component                                                           */
/* ================================================================== */
export default function CustomerViewPage({ pageTitle = 'Customer / View' }) {
  const { id: customerId } = useParams();
  const dispatch           = useDispatch();

  const [customer, setCustomer] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('profile');

  /* ───────────── fetch once on mount ───────────── */
  useEffect(() => {
    dispatch(getByIdCustomerService(customerId))
      .then(r => setCustomer(r.data?.data))
      .catch(e => ToastMe(e.message || 'Failed to load customer', 'error'))
      .finally(() => setLoading(false));
  }, [customerId, dispatch]);

  /* ───────────── handle avatar upload ───────────── */
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !customer?._id) return ToastMe('No customer found', 'error');

    const fd = new FormData();
    fd.append('photo', file);

    try {
      await dispatch(updateCustomerPhotoService(customer._id, fd));
      ToastMe('Profile photo updated', 'success');

      // re‑fetch so we have the new file_name
      const fresh = await dispatch(getByIdCustomerService(customer._id));
      setCustomer(fresh.data?.data);
    } catch (err) {
      ToastMe(err.message || 'Upload failed', 'error');
    }
  };

  /* ───────────── render states ───────────── */
  if (loading)   return <Card className="p-6 text-center">Loading…</Card>;
  if (!customer) return <Card className="p-6 text-center">No customer found.</Card>;

  /* ───────────── build signed avatar URL ─────────────
     folder must match the upload destination:  'profile' */
  const baseAvatar = customer.file_name
    ? getFileUrlSigned(customer.file_name, 'profile')   // ← changed
    : null;

  const avatarUrl = baseAvatar
    ? `${baseAvatar}${baseAvatar.includes('?') ? '&' : '?'}t=${Date.now()}`
    : '/images/default-avatar.png';

  /* ================================================================== */
  /* Mark‑up                                                            */
  /* ================================================================== */
  return (
    <>
      {/* breadcrumb */}
      <div className="pt-[18px]">
        <Breadcrumb pageTitle={pageTitle} />
      </div>

      {/* header */}
      <Card className="rounded-xl border border-[#f0f2f8] bg-white p-6">
        <div className="flex gap-6 items-start">
          {/* avatar */}
          <div className="shrink-0">
            <div className="w-[120px] h-[120px] rounded-[6px] overflow-hidden border border-slate-200">
              <img
                src={avatarUrl}
                alt={customer.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* details + upload button */}
          <div className="flex flex-col">
            <h2 className="text-[28px] font-bold tracking-tight text-slate-900">
              {customer.name}
            </h2>

            <button
              onClick={() => document.getElementById('photoUpload').click()}
              className="mt-6 inline-flex items-center gap-2 h-[44px] w-[220px]
                         rounded-[6px] bg-[#0065ff] text-white hover:bg-[#0056e0]"
            >
              <RiImageEditLine className="text-[20px]" />
              Upload New Photo
            </button>

            <input
              id="photoUpload"
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </div>
        </div>
      </Card>

      {/* tabs */}
      <div className="mt-6 px-6">
        <ul className="flex space-x-6">
          {TAB_ITEMS.map(({ key, label, Icon }) => (
            <li key={key}>
              <button
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition ${
                  tab === key
                    ? 'bg-[#0f62fe] text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="text-base" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* tab content */}
      <div className="mt-6">
        {tab === 'profile'  && <ProfileTab  customer={customer} />}
        {tab === 'security' && <SecurityTab customerId={customerId} />}
        {tab === 'address'  && <AddressTab />}
        {tab === 'orders'   && <OrdersTab  customerId={customerId} />}
        {tab === 'credit'   && <CreditTab  customerId={customerId} />}
      </div>
    </>
  );
}
